/**
 * React Hook for Advanced Resume Analysis
 * Provides easy access to AI-powered resume analysis features
 */

import { t } from "@lingui/macro";
import { TOKEN_COSTS } from "@stylingcv/config";
import { useCallback, useState } from "react";

import { useTokenModal } from "../../contexts/token-modal-context";
import { useToast } from "../../hooks/use-toast";
import type { AdvancedAnalysisResult } from "../../services/smart-ai/advanced-resume-analysis";
import { useAuthStore } from "../../stores/auth";
import { useCentralizedTokens } from "../use-centralized-tokens";

export const useAdvancedResumeAnalysis = (onNavigation?: () => void) => {
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState<AdvancedAnalysisResult | null>(null);
  const { consumeTokens, tokenBalance } = useCentralizedTokens();
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const { toast } = useToast();

  // Use the global token modal context
  const { showTokenModal } = useTokenModal();

  /**
   * Perform comprehensive resume analysis using real AI services
   */
  const analyzeResume = useCallback(
    async (
      resumeData: Record<string, any>,
      jobDescription: string,
      targetRole: string,
      experienceLevel = "mid-level",
    ): Promise<AdvancedAnalysisResult | null> => {
      if (loading) return null;

      const cost = TOKEN_COSTS.ADVANCED_RESUME_ANALYSIS;

      // Check if user has enough tokens BEFORE calling API (consistent with other AI features)
      if (tokenBalance < cost) {
        showTokenModal({
          requiredTokens: cost,
          currentTokens: tokenBalance,
          featureName: "Advanced Resume Analysis",
          onNavigation, // Close parent dialog when navigating to pricing
        });
        return null;
      }

      setLoading(true);

      try {
        toast({
          title: t`Analysis Started`,
          description: t`Advanced resume analysis initiated. Requirements: ${cost} tokens.`,
          variant: "default",
        });

        console.log("🔍 Starting REAL advanced resume analysis...");

        // Use the real hybrid service for comprehensive AI analysis
        const { HybridAdvancedResumeAnalysisService } = await import(
          "../../services/smart-ai/advanced-resume-analysis-hybrid"
        );
        const hybridService = HybridAdvancedResumeAnalysisService.getInstance();

        console.log("🚀 Calling Hybrid AI Service...");
        const result = await hybridService.analyzeResume(
          resumeData,
          jobDescription,
          targetRole,
          experienceLevel,
        );

        console.log("📊 Real AI Analysis result:", result);

        if (result) {
          setAnalysisData(result);
          console.log("✅ Real AI analysis data set successfully");
          toast({
            title: t`AI Analysis Complete`,
            description: t`ATS Score: ${result.atsOptimization.overallScore}% | Job Match: ${result.jobMatchAnalysis.overallMatch}%`,
            variant: "default",
          });
          return result;
        } else {
          toast({
            title: t`Analysis Failed`,
            description: t`Unable to complete the AI analysis. Please try again.`,
            variant: "error",
          });
          return null;
        }
      } catch (error) {
        console.error("❌ Real AI Analysis error:", error);
        toast({
          title: t`AI Analysis Error`,
          description:
            error instanceof Error
              ? error.message
              : t`An unexpected error occurred during AI analysis.`,
          variant: "error",
        });
        return null;
      } finally {
        setLoading(false);
      }
    },
    [loading, consumeTokens, isLoggedIn, toast, showTokenModal, tokenBalance],
  );

  /**
   * Clear analysis data
   */
  const clearAnalysis = useCallback(() => {
    setAnalysisData(null);
  }, []);

  /**
   * Get analysis summary
   */
  const getAnalysisSummary = useCallback(() => {
    if (!analysisData) return null;

    return {
      atsScore: analysisData.atsOptimization.overallScore,
      jobMatch: analysisData.jobMatchAnalysis.overallMatch,
      recommendationsCount:
        (analysisData.personalizedRecommendations?.immediate?.length || 0) +
        (analysisData.personalizedRecommendations?.longTerm?.length || 0),
      hasData: true,
    };
  }, [analysisData]);

  return {
    loading,
    analysisData,
    analyzeResume,
    clearAnalysis,
    getAnalysisSummary,
  };
};
